import { createSlice, PayloadAction } from '@reduxjs/toolkit'

export type ModalName =
  | 'aircraftTimeModal'
  | 'tagModal'
  | 'aircraftSelectorModal'
  | 'confirmationModal'
  | 'auditLogModal'
  | 'maintenanceItemSubmitChangesModal'
  | 'example'
  | 'bulkComplianceEditModal'
  | 'addCustomWorkItemModal'
  | 'aircraftUsageParameterModal'
  | 'userSelectorModal'
  | 'complianceEventLogsModal'
  | 'underRevisionWarningModal'
  | 'vendorDrawer'
  | 'requestPartsModal'
  | 'requestServiceModal'
  | 'addressEditorModal'
  | 'shippingMethodEditorModal'
  | 'addPartLineItemModal'
  | 'addServiceLineItemModal'
  | 'addLineItemModal'
  | 'purchaseOrderReviewModal'
  | 'rejectItemModal'
  | 'sendPurchaseOrderModal'
  | 'vendorEditorModal'
  | 'requestedLineItemsPickerModal'
  | 'purchaseOrderPdfViewModal'
  | 'saveLaborTransactionModal'
  | 'laborTransactionsModal'
  | 'printLogbookModal'
  | 'printWorkCardModal'
  | 'purchaseOrderComplianceModal'
  | 'addMtxItemsToPurchaseOrderModal'
  | 'bulkReadyForReviewReassignmentModal'
  | 'addReferenceModal'
  | 'viewReferenceModal'
  | 'productSelectorModal'
  | 'addAircraftModal'
  | 'workPerformedDrawer'
  | 'partTransactionDrawer'
  | 'requestPartDrawer'
  | 'addCustomTaskDrawer'
  | 'workflowTasksBulkEditDrawer'
  | 'workflowAircraftDetailsDrawer'
  | 'workflowAddDiscrepancyDrawer'
  | 'bulkComplianceCompletedModal'
  | 'createLogbookEntryModal'
  | 'workOrderCompletedModal'
  | 'workOrderDetailDrawer'
  | 'editLaborDetailsDrawer'
  | 'viewLogbookEntryModal'
  | 'singleComplianceDrawer'
  | 'referenceFeedbackModal'

  // inventory modals and drawers
  | 'ingestInventoryModal'
  | 'vendorEditorDrawer'
  | 'createProductDrawer'

  // inventory item lifecycle event drawers
  | 'customLifecycleEventDrawer'
  | 'toolCalibrationLifecycleEventDrawer'
  | 'ingestRentalLifecycleEventDrawer'
  | 'returnRentalLifecycleEventDrawer'
  | 'invoiceViewModal'
  | 'lowStockPickerDrawer'
  | 'inviteUserDrawer'
  | 'rolePermissionsInfoDrawer'
  | 'addPermissionsDrawer'
  | 'cmdk'
  | 'aiAnswerModal'
  | 'iframe'

// Type for Modal state
interface BaseModalProps {
  isOpen: boolean
  data?: Record<string, unknown>
  onClose?: () => void
}

interface ModalState {
  modals: {
    [name: string]: BaseModalProps
    confirmationModal?: BaseModalProps & {
      isOpen: boolean
      data?: {
        message?: string
        richMessage?: string
        discriminator?: string
        variables?: unknown
      }
    }
  }
  openingOrder: Array<{
    name: ModalName
    discriminator?: string
    isClosing?: boolean
  }>
}

// Initial state
const initialState: ModalState = {
  modals: {
    aircraftTimeModal: {
      data: { loading: false, disableCreate: false, disablePick: false },
      isOpen: false,
    },
  },
  openingOrder: [],
}

export const getModalKey = (name: ModalName, discriminator?: string) => {
  return discriminator ? `${name}_${discriminator}` : name
}

const modalsSlice = createSlice({
  name: 'modals',
  initialState,
  reducers: {
    openModal: (
      state,
      action: PayloadAction<{
        name: ModalName
        data?: { discriminator?: string } & Record<string, unknown>
        onClose?: () => void
      }>
    ) => {
      const { name, data, onClose } = action.payload
      const discriminator = data?.discriminator
      const modalKey = getModalKey(name, discriminator)

      // If the modal does not exist, create it and set it to open.
      if (!state.modals[modalKey]) {
        state.modals[modalKey] = { isOpen: true, data, onClose }
      }
      // If the modal does exist, set it to open and update its data.
      else {
        state.modals[modalKey].isOpen = true
        state.modals[modalKey].data = data
        state.modals[modalKey].onClose = onClose
      }

      // Add to opening order
      state.openingOrder.push({ name, discriminator })
    },
    closeModal: (
      state,
      action: PayloadAction<{ name: ModalName; discriminator?: string }>
    ) => {
      const { name, discriminator } = action.payload
      const modalKey = getModalKey(name, discriminator)

      // If the modal exists, set it to closed, trigger onClose callback and reset its data.
      if (state.modals[modalKey]) {
        state.modals[modalKey].isOpen = false
        if (state.modals[modalKey].onClose) {
          state.modals[modalKey].onClose()
        }
        state.modals[modalKey].data = {}
        state.modals[modalKey].onClose = undefined
      }

      // Mark as closing in the opening order
      const modalIndex = state.openingOrder.findIndex(
        (modal) => modal.name === name && modal.discriminator === discriminator
      )
      if (modalIndex !== -1) {
        state.openingOrder[modalIndex].isClosing = true
      }
    },
    removeClosedModals: (state) => {
      state.openingOrder = state.openingOrder.filter(
        (modal) => !modal.isClosing
      )
    },
    setModalData: (
      state,
      action: PayloadAction<{
        name: ModalName
        data: { discriminator?: string } & Record<string, unknown>
      }>
    ) => {
      const { name, data } = action.payload
      const modalKey = getModalKey(name, data?.discriminator)

      if (!state.modals[modalKey]) {
        state.modals[modalKey] = { isOpen: false, data }
      } else {
        state.modals[modalKey].data = {
          ...state.modals[modalKey].data,
          ...data,
        }
      }
    },
  },
})

export const { openModal, closeModal, removeClosedModals, setModalData } =
  modalsSlice.actions

export default modalsSlice.reducer
